import React, { useState } from 'react';
import { Stack, Button, Typography, Tooltip, useTheme } from '@mui/material';
import { POItemShipmentGroup } from '@domain/purchasing';
import { TransportMethodIcon } from '@features/shipments/components/shared/TransportMethodIcon';
import { differenceInDays, format, startOfDay } from 'date-fns';
import { Icon, IconSize } from '@shared/icons';


interface PurchaseOrderGroupProps {
    group: POItemShipmentGroup;
    onChangeShipment: (group: POItemShipmentGroup) => void;
    children?: React.ReactNode;
    itemHeight?: number;
}

export const PurchaseOrderGroup: React.FC<PurchaseOrderGroupProps> = ({
    group,
    onChangeShipment,
    children,
    itemHeight,
}) => {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);

    if (!group.shipment) return null;

    return (
        <Stack direction="row" alignItems="flex-start" gap={2}>

            <Stack direction="column" alignItems="center" height="100%" position="relative">
                <TransportMethodIcon badge={group.shipment.draft ? 'new' : undefined} size={40} transportMethod={group.shipment.transportMethod} />

                <div style={{
                    backgroundColor: theme.palette.divider,
                    width: '3px',
                    height: (itemHeight || 88) * group.items.length - 32,
                    flexGrow: 1,
                    marginBottom: (itemHeight || 88) / 2,
                    position: 'relative',
                }}>

                    {group.items.map((_, itemIndex) => {
                        return (
                            <div key={itemIndex} style={{
                                backgroundColor: theme.palette.divider,
                                width: '13px',
                                height: '13px',
                                position: 'absolute',
                                top: `${(itemIndex + 1) * (itemHeight || 88) - 32}px`,
                                left: '-5px',
                                zIndex: 1000,
                                borderRadius: '10px',
                            }} />
                        )
                    })}

                </div>
            </Stack>


            <Stack direction="column" gap={2}
                flexGrow={1}
                onMouseOver={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <Stack direction="row" gap={2} height="40px" alignItems="center" justifyContent="space-between">

                    <Stack direction="column" gap={0} alignItems="flex-start">



                        <Stack direction="row" gap={2} alignItems="center" flexGrow={1}>

                            <Typography variant="body2" color="text.primary">
                                Departure: {format(group.shipment.departureDate, 'dd MMM')}
                            </Typography>

                            <Tooltip title={
                                <>
                                    <Typography variant="body2">
                                        Shipment: {differenceInDays(startOfDay(group.shipment.arrivalDate), startOfDay(group.shipment.departureDate))} days
                                    </Typography>

                                    <Typography variant="body2">
                                        Processing: {differenceInDays(startOfDay(group.shipment.processedDate), startOfDay(group.shipment.arrivalDate))} days
                                    </Typography>
                                </>
                            }>
                                <Typography variant="body2" color="text.secondary">
                                    + {differenceInDays(startOfDay(group.shipment.processedDate), startOfDay(group.shipment.departureDate))} days
                                </Typography>
                            </Tooltip>

                            <Typography variant="body2" color="text.primary">
                                Available: {format(group.shipment.processedDate, 'dd MMM')} in {group.shipment.destinationWarehouse?.name}
                            </Typography>

                        </Stack>

                    </Stack>


                    <Stack direction="row" gap={1} alignItems="center">

                        <Typography variant="body2" color="text.secondary" maxWidth="200px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                            {group.shipment.name}
                        </Typography>

                        {isHovered && (
                            <Button color="secondary" onClick={() => onChangeShipment(group)} endIcon={<Icon icon="ArrowRefresh02" size={IconSize.SMALL} />}>
                                Change shipment
                            </Button>
                        )}
                    </Stack>
                </Stack>

                {children}
            </Stack>
        </Stack>

    );
};
